import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Lock, Minus, PenLine, Plus, ShieldCheck } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { documentsPathForUser, homePathForUser } from "@/lib/roleHome";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
const NDA_PDF_URL = `${API_BASE}/auth/legal/nda.pdf`;

/**
 * Standalone NDA signing page reached from the "Send NDA" email/notification a
 * buyer receives. Buyers are exempt from the onboarding NDA gate, so they can't
 * reach LegalOnboarding's NDA step — this page lets any signed-in user sign the
 * active NDA on demand and reuses the same POST /auth/legal/sign-nda endpoint
 * (which emails the finalized copy and reflects "Signed" under Documents).
 */
export default function SignNdaPage() {
  const { user, loading, signNda } = useAuth();
  const [signatureName, setSignatureName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const observerRef = useRef<ResizeObserver | null>(null);
  const toast = useToast();
  const navigate = useNavigate();

  const setPdfContainer = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    const measure = () => setContainerWidth(node.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  const pageWidth = Math.max(280, (containerWidth - 24) * zoom);
  const pageHeight = pageWidth * 1.294;

  // A buyer who has already signed the current NDA doesn't need this page.
  const alreadySigned = Boolean(user?.nda_verified);

  useEffect(() => {
    if (!loading && !user) navigate("/sign-in", { replace: true });
  }, [loading, user, navigate]);

  const handleSignNda = async () => {
    if (signatureName.trim().length < 3) {
      toast.error("Please type your full legal name as digital signature");
      return;
    }
    setSubmitting(true);
    try {
      await signNda({ nda_signature_name: signatureName.trim() });
      toast.success("NDA signed successfully");
      setSigned(true);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Unable to sign NDA");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-background py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const done = signed || alreadySigned;

  return (
    <main className="flex-1 bg-muted/40">
      {/* Hero */}
      <div className="bg-background border-b border-border/80">
        <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex items-start gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              {done ? <ShieldCheck className="h-7 w-7" /> : <Lock className="h-7 w-7" />}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
                {done ? "Your NDA is signed" : "Sign your NDA"}
              </h1>
              <p className="mt-1.5 text-base text-muted-foreground leading-relaxed">
                {done
                  ? "You're all set — confidential deal details are now unlocked for you."
                  : "Review the Non-Disclosure Agreement below, then add your digital signature to unlock confidential deal details."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8 space-y-6">
        {done ? (
          <Card className="rounded-2xl border-border/80 shadow-sm">
            <CardContent className="py-10 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
              <p className="mt-3 text-base font-medium text-foreground">
                Non-Disclosure Agreement signed.
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                A finalized copy has been emailed to you and saved under Documents in your settings.
              </p>
              {/* Role-resolved, not hardcoded to /buyer/: this page is reached by
                  advisors and sellers too, and a buyer-only path would bounce
                  them off RoleRoute to "/" the moment they finished signing. */}
              <div className="mt-6 flex items-center justify-center gap-3">
                <Link to={user ? documentsPathForUser(user) : "/"}>
                  <Button variant="outline" className="gap-2">View in Documents</Button>
                </Link>
                <Link to={user ? homePathForUser(user) : "/"}>
                  <Button className="gap-2">Go to your dashboard</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-2xl border-border/80 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <PenLine className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Non-Disclosure Agreement</CardTitle>
                  <CardDescription className="text-sm">
                    Read the NDA below, then provide your digital signature to continue.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/30">
                {/* Zoom toolbar */}
                <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-muted/50 px-3 py-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    {numPages > 0 ? `${numPages} page${numPages === 1 ? "" : "s"}` : "Document"}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                      disabled={zoom <= 0.5}
                      aria-label="Zoom out"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-12 text-center text-xs font-semibold tabular-nums text-foreground">
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      type="button"
                      onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}
                      disabled={zoom >= 2.5}
                      aria-label="Zoom in"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div ref={setPdfContainer} className="h-[32rem] overflow-auto bg-muted/20 p-3">
                  <Document
                    file={NDA_PDF_URL}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    loading={
                      <div className="flex h-[28rem] flex-col items-center justify-center gap-3 text-muted-foreground">
                        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                        <p className="text-sm">Loading NDA…</p>
                      </div>
                    }
                    error={<p className="px-2 py-4 text-sm text-destructive">Unable to load the NDA document. Please refresh and try again.</p>}
                    className="mx-auto flex w-max min-w-full flex-col items-center gap-3"
                  >
                    {containerWidth > 0 &&
                      Array.from({ length: numPages }, (_, i) => (
                        <Page
                          key={i}
                          pageNumber={i + 1}
                          width={pageWidth}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          loading={
                            <div
                              style={{ width: pageWidth, height: pageHeight }}
                              className="flex items-center justify-center rounded-md bg-card shadow-sm ring-1 ring-border/40"
                            >
                              <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                            </div>
                          }
                          className="overflow-hidden rounded-md shadow-sm ring-1 ring-border/40"
                        />
                      ))}
                  </Document>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ndaSignatureName" className="text-sm font-medium">
                  Digital Signature — type your full legal name
                </Label>
                <Input
                  id="ndaSignatureName"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="e.g. Jane A. Smith"
                  className="font-medium"
                />
                <p className="text-xs text-muted-foreground">
                  By typing your name, you are providing a legally binding digital signature.
                </p>
              </div>

              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-muted-foreground">
                  Signing unlocks confidential deal details across the marketplace.
                </p>
                <Button
                  onClick={handleSignNda}
                  disabled={submitting || signatureName.trim().length < 3}
                  className="gap-2"
                >
                  <PenLine className="h-4 w-4" />
                  Sign &amp; Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Need help?{" "}
          <Link to="/support" className="font-medium text-primary underline-offset-2 hover:underline">
            Contact Support
          </Link>
          .
        </p>
      </div>
    </main>
  );
}
